import React, { useState, useEffect } from 'react';
import { Check, X, UserCheck, Type, Search } from 'lucide-react';
import { adminService } from '../../services';
import Input from '../ui/Input';

/**
 * Editor do piloto de um participante de evento especial. O piloto pode ser
 * vinculado a um usuário do sistema (recebe pagamento BC) ou ser só um texto
 * livre (apenas informativo, não recebe pagamento). A busca encontra o
 * usuário pelo nome dele ou de um dos seus personagens. takenPilots mapeia
 * pilotUserId -> nome do personagem que ele já pilota neste evento.
 */
const PilotEditor = ({ participant, takenPilots = {}, onSave, onCancel }) => {
  const initialUser = participant.pilotUserId
    ? { id: participant.pilotUserId, name: participant.pilotUser?.name || participant.pilot }
    : null;

  const [mode, setMode] = useState(participant.pilot && !participant.pilotUserId ? 'text' : 'user');
  const [text, setText] = useState(participant.pilotUserId ? '' : participant.pilot || '');
  const [selectedUser, setSelectedUser] = useState(initialUser);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const term = search.trim();
    if (mode !== 'user' || term.length < 2) {
      setResults([]);
      return undefined;
    }

    const timeout = setTimeout(async () => {
      try {
        setSearching(true);
        const data = await adminService.getAllUsers({ search: term, status: 'APPROVED', limit: 8 });
        setResults(data.users || []);
      } catch (err) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [search, mode]);

  const handleSave = () => {
    if (mode === 'user') {
      if (selectedUser) {
        onSave({ pilotUserId: selectedUser.id });
      } else {
        onSave({ pilot: '', pilotUserId: null });
      }
    } else {
      onSave({ pilot: text.trim(), pilotUserId: null });
    }
  };

  const modeButton = (value, Icon, label) => (
    <button
      type="button"
      onClick={() => setMode(value)}
      className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs border transition-all ${
        mode === value
          ? 'bg-orange-500/20 text-orange-400 border-orange-500/30'
          : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'
      }`}
    >
      <Icon className="w-3 h-3"/>
      {label}
    </button>
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {modeButton('user', UserCheck, 'Usuário')}
        {modeButton('text', Type, 'Texto livre')}
      </div>

      <div className="flex gap-2 items-start">
        <div className="flex-1 min-w-0">
          {mode === 'user' ? (
            selectedUser ? (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-green-500/10 border border-green-500/30 text-sm text-green-400">
                <UserCheck className="w-4 h-4 shrink-0"/>
                <span className="flex-1 truncate">{selectedUser.name}</span>
                <button
                  type="button"
                  onClick={() => setSelectedUser(null)}
                  className="text-gray-400 hover:text-red-400"
                  title="Trocar usuário"
                >
                  <X className="w-3 h-3"/>
                </button>
              </div>
            ) : (
              <div className="relative">
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar usuário ou personagem..."
                  className="text-sm"
                  leftIcon={<Search className="w-4 h-4"/>}
                  autoFocus
                />
                {(results.length > 0 || searching) && (
                  <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto rounded-xl bg-slate-900 border border-white/10 shadow-xl">
                    {searching && <p className="px-3 py-2 text-xs text-gray-500">Buscando...</p>}
                    {results.map(u => {
                      const takenBy = takenPilots[u.id];
                      return (
                      <button
                        key={u.id}
                        type="button"
                        disabled={Boolean(takenBy)}
                        title={takenBy ? `Já é piloto de ${takenBy} neste evento` : undefined}
                        onClick={() => {
                          setSelectedUser({ id: u.id, name: u.name });
                          setSearch('');
                          setResults([]);
                        }}
                        className={`block w-full text-left px-3 py-2 text-sm ${
                          takenBy ? 'text-gray-500 cursor-not-allowed' : 'text-gray-200 hover:bg-white/10'
                        }`}
                      >
                        {u.name}
                        {u.characters?.length > 0 && (
                          <span className="text-xs text-gray-500"> • {u.characters.map(c => c.name).join(', ')}</span>
                        )}
                        {takenBy && (
                          <span className="block text-xs text-orange-400">Já é piloto de {takenBy}</span>
                        )}
                      </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )
          ) : (
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Nome do piloto"
              className="text-sm"
              autoFocus
            />
          )}
        </div>
        <button
          type="button"
          onClick={handleSave}
          className="p-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30 transition-all"
          title="Salvar"
        >
          <Check className="w-4 h-4"/>
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-all text-gray-400"
          title="Cancelar"
        >
          <X className="w-4 h-4"/>
        </button>
      </div>

      {mode === 'text' && (
        <p className="text-xs text-gray-500 italic">Piloto sem vínculo com usuário não recebe pagamento BC.</p>
      )}
    </div>
  );
};

export default PilotEditor;
